// ============================================================================
// Pure logic of the media « Scans » tab (media_access_logs).
//
// Live facts this module encodes (verified 2026-10-02 on the LIRF project):
// - almost every scan comes from a phone opening the badge QR link on the
//   public /media-verify page: no scanner_id, no match_id, a browser UA;
// - the delegate app scans with scanner_id set, gate_location 'delegate_app'
//   and an okhttp UA; the webapp scan mode (/media/scan) sends 'webapp_gate';
// - since contract v2 the server itself logs robots as gate_location
//   'automated' and adds scan_data.qr_kind ('badge' | 'mobile_qr');
// - test traffic ('node', 'curl') and fetchers (Google-Read-Aloud) are
//   recorded like real scans and must not count as attendance;
// - failure_reason carries the English server reason of
//   media-accreditation-app/supabase/functions/_shared/scanValidation.ts, and
//   'Access granted' on every VALID scan (not a failure).
//
// Shared with the « Matchs » tab (attendance) — keep the input shapes minimal.
// ============================================================================

import type { MediaAccessLog, ScanResultType } from "@/types/media";
import { getLocalizedPersonName, type DisplayMode } from "@/utils/nameHelpers";
import { algiersDayKey } from "@/utils/algiersTime";
import { getTeamDisplayName } from "@/utils/teamNameDisplay";

// ---------------------------------------------------------------- shapes ----

export interface ScanTeam {
    name: string;
    name_ar?: string | null;
}

export interface ScanMatch {
    id: string;
    match_date: string;
    match_time?: string | null;
    game_day?: number | null;
    home_team?: ScanTeam | null;
    away_team?: ScanTeam | null;
}

export interface ScanOrganization {
    name: string;
    name_ar?: string | null;
    /** Deleted organisation: its scans stay, with a « Supprimé » chip. */
    deleted_at?: string | null;
}

export interface ScanPersonnel {
    id: string;
    media_type?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    first_name_ar?: string | null;
    last_name_ar?: string | null;
    photo_url?: string | null;
    profile?: { full_name?: string | null } | null;
    organization?: ScanOrganization | null;
    /** Deleted journalist: their scans stay, with a « Supprimé » chip. */
    deleted_at?: string | null;
}

export interface ScanAccreditation {
    id: string;
    league_id?: string | null;
    season?: string | null;
    match_id?: string | null;
    media_personnel_id?: string | null;
    request_type?: string | null;
    organization_name?: string | null;
    organization?: ScanOrganization | null;
    media_personnel?: ScanPersonnel | null;
    match?: ScanMatch | null;
}

/** One row of the Scans tab, as returned by `useMediaAccessLogs`. */
export interface ScanLog
    extends Omit<MediaAccessLog, "accreditation" | "match"> {
    accreditation?: ScanAccreditation | null;
    match?: ScanMatch | null;
}

/** The minimal fields the source / attendance / duplicate logic reads. */
export interface ScanLike {
    id: string;
    accreditation_id: string | null;
    scan_result: ScanResultType | string;
    scanned_at: string;
    scanner_id?: string | null;
    gate_location?: string | null;
    scan_data?: Record<string, unknown> | null;
}

// ------------------------------------------------------ automated traffic ----

/**
 * UA prefixes of scripts and HTTP libraries (matched case-insensitively at the
 * start of the user agent). `okhttp` is deliberately NOT here: it is the
 * delegate app.
 *
 * Every entry must stay free of `_`, `%`, `*`, `,`, `(`, `)` and `.`: the same
 * list is turned into PostgREST `ilike` filters (see automatedTrafficOrFilter).
 */
export const AUTOMATED_UA_PREFIXES: readonly string[] = [
    "node",
    "curl",
    "wget",
    "python",
    "axios",
    "go-http-client",
    "java/",
    "apache-httpclient",
    "postman",
    "insomnia",
    "httpie",
    "deno",
    "bun/",
    "undici",
    "libwww",
    "php",
    "ruby",
    "powershell",
    "dart/",
];

/** UA fragments of bots, crawlers, fetchers and headless browsers. */
export const AUTOMATED_UA_FRAGMENTS: readonly string[] = [
    "bot/",
    "bot;",
    "crawler",
    "spider",
    "slurp",
    "google-read-aloud",
    "facebookexternalhit",
    "headless",
    "lighthouse",
    "puppeteer",
    "playwright",
    "phantomjs",
    "scrapy",
    "python-requests",
];

/** True for scripts, bots and fetchers; false for browsers, okhttp and an absent UA. */
export function isAutomatedUserAgent(ua?: string | null): boolean {
    const value = (ua ?? "").trim().toLowerCase();
    if (!value) return false;
    if (AUTOMATED_UA_PREFIXES.some((p) => value.startsWith(p))) return true;
    return AUTOMATED_UA_FRAGMENTS.some((f) => value.includes(f));
}

export function scanUserAgent(log: Pick<ScanLike, "scan_data">): string | null {
    const ua = log.scan_data?.["user_agent"];
    return typeof ua === "string" ? ua : null;
}

export type ScanSource = "gate" | "delegate" | "app" | "phone" | "automated";

export const SCAN_SOURCES: readonly ScanSource[] = [
    "gate",
    "delegate",
    "app",
    "phone",
    "automated",
];

/** Checkpoints a client may declare (media_access_logs.gate_location). */
export const GATE_LOCATIONS = {
    /** Scan mode of this webapp (/media/scan), a signed-in gate agent. */
    webappGate: "webapp_gate",
    /** Officials app, the match delegate. */
    delegateApp: "delegate_app",
    /** Media app scanner, or the server default for an authorised caller that sent none. */
    mobileApp: "mobile_app",
    /** Server-set: a verification by anyone who is not a scanner (/media-verify). */
    publicLink: "public_link",
    /** Server-set (contract v2): robot / script user agent. */
    automated: "automated",
} as const;

/**
 * Where a scan came from.
 *
 * - `gate_location = 'automated'` is the server's own verdict (contract v2)
 *   and wins over everything;
 * - a declared checkpoint wins next: webapp scan mode, delegate app, media app;
 * - `public_link` is a mere verification even when the caller was signed in
 *   (scanner_id is then set, but the server refused them the scanner tier) —
 *   it is a phone, unless its UA is a robot (rows logged before v2);
 * - a scanner_id without checkpoint (legacy rows) is the delegate;
 * - otherwise the user agent decides between automated traffic and a phone.
 */
export function scanSource(
    log: Pick<ScanLike, "scanner_id" | "gate_location" | "scan_data">
): ScanSource {
    const gate = log.gate_location ?? null;
    if (gate === GATE_LOCATIONS.automated) return "automated";
    if (gate === GATE_LOCATIONS.webappGate) return "gate";
    if (gate === GATE_LOCATIONS.delegateApp) return "delegate";
    if (gate === GATE_LOCATIONS.mobileApp) return "app";
    const robot = isAutomatedUserAgent(scanUserAgent(log));
    if (gate === GATE_LOCATIONS.publicLink) return robot ? "automated" : "phone";
    if (log.scanner_id) return "delegate";
    return robot ? "automated" : "phone";
}

export function isAutomatedScan(
    log: Pick<ScanLike, "scanner_id" | "gate_location" | "scan_data">
): boolean {
    return scanSource(log) === "automated";
}

/**
 * True when the source records a stadium ENTRY (a gate agent, the delegate or
 * the media-app scanner — the server's scanner tier); a phone on the public
 * link is only a verification.
 */
export function isEntrySource(source: ScanSource): boolean {
    return source === "gate" || source === "delegate" || source === "app";
}

export type ScanQrKind = "badge" | "mobile_qr";

/** `scan_data.qr_kind` (contract v2), or null on older rows. */
export function scanQrKind(log: Pick<ScanLike, "scan_data">): ScanQrKind | null {
    const kind = log.scan_data?.["qr_kind"];
    return kind === "badge" || kind === "mobile_qr" ? kind : null;
}

/**
 * The same rule as `scanSource(...) !== 'automated'`, as ONE element of a
 * PostgREST `or=(…)` group, so the list, its count and the stats exclude
 * automated traffic server-side. A NULL user agent is kept, like the client
 * rule does; a NULL gate_location is kept by the first branch (`neq` alone
 * would drop it — NULL is not "not equal").
 */
export function automatedTrafficOrFilter(): string {
    const ua = "scan_data->>user_agent";
    const notAutomated = [
        ...AUTOMATED_UA_PREFIXES.map((p) => `${ua}.not.ilike.${p}*`),
        ...AUTOMATED_UA_FRAGMENTS.map((f) => `${ua}.not.ilike.*${f}*`),
    ].join(",");
    const g = GATE_LOCATIONS;
    const notServerAutomated = `or(gate_location.is.null,gate_location.neq.${g.automated})`;
    const kept = [
        `gate_location.in.(${g.webappGate},${g.delegateApp},${g.mobileApp})`,
        `and(scanner_id.not.is.null,or(gate_location.is.null,gate_location.neq.${g.publicLink}))`,
        `${ua}.is.null`,
        `and(${notAutomated})`,
    ].join(",");
    return `and(${notServerAutomated},or(${kept}))`;
}

// --------------------------------------------------------------- reasons ----

/** Exact server reasons (scanValidation.ts + scan-accreditation/index.ts). */
const EXACT_REASONS: Record<string, string> = {
    "access granted": "valid",
    "token not found": "token_not_found",
    "invalid token": "token_not_found",
    "token is revoked": "token_revoked",
    "token has expired": "token_expired",
    "accreditation request not found": "accreditation_not_found",
    "badge not issued/downloaded": "badge_not_issued",
    "not yet valid": "not_yet_valid",
    "accreditation expired": "accreditation_expired",
    "match postponed": "match_postponed",
    "match cancelled": "match_cancelled",
    "authorization not for this match": "wrong_match",
    "holder participation suspended": "participation_suspended",
};

/** Reasons carrying a variable suffix ("Accreditation is rejected", …). */
const PREFIX_REASONS: [string, string][] = [
    ["token purpose not allowed", "token_purpose"],
    ["accreditation is ", "accreditation_not_approved"],
    ["not approved for ", "wrong_zone"],
];

const RESULT_FALLBACK: Record<string, string> = {
    invalid: "invalid",
    expired: "expired",
    revoked: "revoked",
    wrong_match: "wrong_match",
    wrong_zone: "wrong_zone",
    capacity_exceeded: "capacity_exceeded",
};

/** Every key `scanReasonKey` can return (under `scan.reasons.*`). */
export const SCAN_REASON_KEYS: readonly string[] = Array.from(
    new Set([
        "valid",
        "unknown",
        ...Object.values(EXACT_REASONS),
        ...PREFIX_REASONS.map(([, key]) => key),
        ...Object.values(RESULT_FALLBACK),
    ])
);

/**
 * i18n key suffix (`scan.reasons.<key>`) of a scan outcome. A valid scan is
 * always `valid` — 'Access granted' is not a failure.
 */
export function scanReasonKey(
    scanResult: ScanResultType | string | null | undefined,
    failureReason?: string | null
): string {
    if (scanResult === "valid") return "valid";
    const reason = (failureReason ?? "").trim().toLowerCase();
    if (reason && reason !== "access granted") {
        const exact = EXACT_REASONS[reason];
        if (exact) return exact;
        const prefixed = PREFIX_REASONS.find(([prefix]) => reason.startsWith(prefix));
        if (prefixed) return prefixed[1];
    }
    return RESULT_FALLBACK[String(scanResult ?? "")] ?? "unknown";
}

// ------------------------------------------------- /media-verify errors ----

/** scan-accreditation `error` codes → `scan.reasons.*` / `scan.verify_errors.*`. */
const VERIFY_CODE_REASONS: Record<string, string> = {
    TOKEN_INVALID: "reasons.token_not_found",
    TOKEN_REVOKED: "reasons.token_revoked",
    TOKEN_EXPIRED: "reasons.token_expired",
    INVALID_TOKEN_PURPOSE: "reasons.token_purpose",
    ACCREDITATION_NOT_FOUND: "reasons.accreditation_not_found",
    ACCREDITATION_NOT_APPROVED: "reasons.accreditation_not_approved",
    BADGE_NOT_ISSUED: "reasons.badge_not_issued",
    PARTICIPATION_SUSPENDED: "reasons.participation_suspended",
    ACCREDITATION_NOT_YET_VALID: "reasons.not_yet_valid",
    ACCREDITATION_EXPIRED: "reasons.accreditation_expired",
    MATCH_POSTPONED: "reasons.match_postponed",
    MATCH_CANCELLED: "reasons.match_cancelled",
    INVALID_MATCH: "reasons.wrong_match",
    INVALID_ZONE: "reasons.wrong_zone",
    TOKEN_REQUIRED: "verify_errors.token_required",
    RATE_LIMITED: "verify_errors.rate_limited",
    SYSTEM_ERROR: "verify_errors.server_error",
};

/**
 * Translation key (namespace `media`, under `scan.`) for a /media-verify
 * failure: a scan-accreditation error code, an `HTTP 4xx/5xx` message thrown by
 * the service, or a network failure. Never returns a raw code.
 */
export function verifyErrorKey(codeOrMessage?: string | null): string {
    const value = (codeOrMessage ?? "").trim();
    const mapped = VERIFY_CODE_REASONS[value.toUpperCase()];
    if (mapped) return `scan.${mapped}`;
    const http = /^HTTP\s+(\d{3})/i.exec(value);
    if (http) {
        const status = Number(http[1]);
        if (status === 429) return "scan.verify_errors.rate_limited";
        return status >= 500
            ? "scan.verify_errors.server_error"
            : "scan.verify_errors.request_error";
    }
    if (/failed to fetch|networkerror|network request failed|load failed/i.test(value)) {
        return "scan.verify_errors.network_error";
    }
    return "scan.verify_errors.generic";
}

// ------------------------------------------------------------ duplicates ----

export const DUPLICATE_SCAN_WINDOW_MS = 10 * 60 * 1000;

/**
 * Ids of valid scans that re-scan the same accreditation within `windowMs` of a
 * previous valid scan (sliding: each valid scan restarts the window). Automated
 * traffic is neither flagged nor used as an anchor. Input order is irrelevant.
 */
export function markDuplicateScans(
    logs: readonly ScanLike[],
    windowMs: number = DUPLICATE_SCAN_WINDOW_MS
): Set<string> {
    const duplicates = new Set<string>();
    const candidates = logs
        .filter(
            (log) =>
                log.scan_result === "valid" &&
                log.accreditation_id &&
                !isAutomatedScan(log) &&
                !Number.isNaN(Date.parse(log.scanned_at))
        )
        .map((log) => ({ log, at: Date.parse(log.scanned_at) }))
        .sort((a, b) => a.at - b.at || a.log.id.localeCompare(b.log.id));

    const lastValidAt = new Map<string, number>();
    for (const { log, at } of candidates) {
        const key = log.accreditation_id as string;
        const previous = lastValidAt.get(key);
        if (previous !== undefined && at - previous <= windowMs) {
            duplicates.add(log.id);
        }
        lastValidAt.set(key, at);
    }
    return duplicates;
}

// ----------------------------------------------------------------- match ----

type MatchCarrier = {
    match_id?: string | null;
    match?: ScanMatch | null;
    accreditation?: Pick<ScanAccreditation, "match_id" | "match"> | null;
};

/** The scanned match, else the accreditation's own match. */
export function effectiveMatch(log: MatchCarrier): ScanMatch | null {
    return log.match ?? log.accreditation?.match ?? null;
}

export function effectiveMatchId(log: MatchCarrier): string | null {
    return (
        log.match_id ??
        log.match?.id ??
        log.accreditation?.match_id ??
        log.accreditation?.match?.id ??
        null
    );
}

// ---------------------------------------------------------------- holder ----

/**
 * Holder name in ONE language (displayMode): the media_personnel identity
 * first (it exists for account-less journalists too), then the profile name.
 * Null when neither is known — the view shows « Titulaire inconnu ».
 */
export function holderName(
    personnel: Pick<
        ScanPersonnel,
        "first_name" | "last_name" | "first_name_ar" | "last_name_ar"
    > | null | undefined,
    profile: { full_name?: string | null } | null | undefined,
    displayMode: DisplayMode
): string | null {
    const fromPersonnel = personnel
        ? getLocalizedPersonName(
            {
                firstName: personnel.first_name,
                lastName: personnel.last_name,
                firstNameAr: personnel.first_name_ar,
                lastNameAr: personnel.last_name_ar,
            },
            displayMode
        )
        : "";
    if (fromPersonnel) return fromPersonnel;
    const fromProfile = (profile?.full_name ?? "").trim();
    return fromProfile || null;
}

/** Organisation label in one language: the personnel org, then the accreditation's. */
export function holderOrganization(
    accreditation: Pick<
        ScanAccreditation,
        "organization" | "organization_name" | "media_personnel"
    > | null | undefined,
    displayMode: DisplayMode
): string | null {
    const org =
        accreditation?.media_personnel?.organization ?? accreditation?.organization;
    if (org) {
        const label =
            displayMode === "arabic" && org.name_ar?.trim() ? org.name_ar : org.name;
        if (label?.trim()) return label;
    }
    return accreditation?.organization_name?.trim() || null;
}

// ------------------------------------------------------------ attendance ----

export interface AccreditationAttendance {
    firstValidAt: string;
    lastValidAt: string;
    validCount: number;
    sources: Set<ScanSource>;
    /** First valid scan by an entry source (gate / delegate / app), or null when only phones verified it. */
    firstEntryAt: string | null;
    /** QR kinds seen (contract v2 rows only). */
    qrKinds: Set<ScanQrKind>;
}

/** Valid, non-automated scans per accreditation (first scan, count, sources). */
export function attendanceByAccreditation(
    logs: readonly ScanLike[]
): Map<string, AccreditationAttendance> {
    const result = new Map<string, AccreditationAttendance>();
    for (const log of logs) {
        if (log.scan_result !== "valid" || !log.accreditation_id) continue;
        const source = scanSource(log);
        if (source === "automated") continue;
        const at = Date.parse(log.scanned_at);
        if (Number.isNaN(at)) continue;
        const isEntry = isEntrySource(source);
        const qrKind = scanQrKind(log);
        const entry = result.get(log.accreditation_id);
        if (!entry) {
            result.set(log.accreditation_id, {
                firstValidAt: log.scanned_at,
                lastValidAt: log.scanned_at,
                validCount: 1,
                sources: new Set([source]),
                firstEntryAt: isEntry ? log.scanned_at : null,
                qrKinds: new Set(qrKind ? [qrKind] : []),
            });
            continue;
        }
        entry.validCount += 1;
        entry.sources.add(source);
        if (qrKind) entry.qrKinds.add(qrKind);
        if (
            isEntry &&
            (entry.firstEntryAt === null || at < Date.parse(entry.firstEntryAt))
        ) {
            entry.firstEntryAt = log.scanned_at;
        }
        if (at < Date.parse(entry.firstValidAt)) entry.firstValidAt = log.scanned_at;
        if (at > Date.parse(entry.lastValidAt)) entry.lastValidAt = log.scanned_at;
    }
    return result;
}

// ----------------------------------------------------------------- stats ----

export interface ScanSummary {
    total: number;
    valid: number;
    refused: number;
    /** Distinct journalists with at least one valid scan. */
    present: number;
    automatedExcluded: number;
    zoneBreakdown: Record<string, number>;
}

/**
 * Tile figures. Automated traffic is always left out (and counted apart);
 * a journalist is one media_personnel, falling back to the accreditation.
 */
export function summarizeScans(
    logs: readonly (ScanLike & {
        zone_accessed?: string | null;
        accreditation?: { media_personnel_id?: string | null } | null;
    })[]
): ScanSummary {
    const summary: ScanSummary = {
        total: 0,
        valid: 0,
        refused: 0,
        present: 0,
        automatedExcluded: 0,
        zoneBreakdown: {},
    };
    const people = new Set<string>();
    for (const log of logs) {
        if (isAutomatedScan(log)) {
            summary.automatedExcluded += 1;
            continue;
        }
        summary.total += 1;
        if (log.scan_result === "valid") {
            summary.valid += 1;
            const person =
                log.accreditation?.media_personnel_id ?? log.accreditation_id;
            if (person) people.add(person);
        } else {
            summary.refused += 1;
        }
        if (log.zone_accessed) {
            summary.zoneBreakdown[log.zone_accessed] =
                (summary.zoneBreakdown[log.zone_accessed] ?? 0) + 1;
        }
    }
    summary.present = people.size;
    return summary;
}

// ------------------------------------------------------------ Algiers day ----

/** Shift a "YYYY-MM-DD" key by whole days (calendar arithmetic, UTC-safe). */
function shiftDayKey(dayKey: string, days: number): string {
    const [y, m, d] = dayKey.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d + days));
    return date.toISOString().slice(0, 10);
}

/**
 * [from, to) instants of an Algiers calendar day ("YYYY-MM-DD"). Algeria is
 * UTC+01:00 all year (no DST), see utils/algiersTime.ts.
 */
export function algiersDayBounds(dayKey: string): { from: string; to: string } | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return null;
    return {
        from: `${dayKey}T00:00:00+01:00`,
        to: `${shiftDayKey(dayKey, 1)}T00:00:00+01:00`,
    };
}

/** Today's Algiers day key. */
export function algiersToday(now: Date = new Date()): string {
    return algiersDayKey(now);
}

export function isScanToday(scannedAt: string, now: Date = new Date()): boolean {
    const key = algiersDayKey(scannedAt);
    return key !== "" && key === algiersDayKey(now);
}

/** « Home vs Away » in one language (displayMode), or null without a match. */
export function matchTeamsLabel(
    match: Pick<ScanMatch, "home_team" | "away_team"> | null | undefined,
    displayMode: DisplayMode,
    vsLabel: string
): string | null {
    if (!match) return null;
    const team = (t: ScanTeam | null | undefined) =>
        t ? getTeamDisplayName(t, displayMode) : "—";
    return `${team(match.home_team)} ${vsLabel} ${team(match.away_team)}`;
}
